<script setup>
    import { ref, onMounted  } from 'vue';
    import { VueTelInput } from 'vue-tel-input';
    import { useBookingStore } from '~/stores/BookingStore';
    import { useCarsStore } from '~/stores/CarsStore';
    import { RecaptchaV2 } from "vue3-recaptcha-v2";
    import { toast } from 'vue3-toastify'

    const booking = useBookingStore();
    const cars = useCarsStore();

    const defaultCountry = 'ru';

    const isOpen = ref(false);

    const loading = ref(false);

    const openPopup = () => {
        isOpen.value = true;
        clientName.value = null;
        clientPhone.value = null;
        recaptchaToken.value = null;
    };
    const closePopup = () => { isOpen.value = false; };

    defineExpose({ openPopup });

    const props = defineProps({
        car: { default: {}, type: Object },
        locationName: { default: {}, type: String }
    });

    const clientPhonePrefix = ref('');
    const theme = ref<'light' | 'dark'>('light')
    const recaptchaToken = ref('');

    const onSelect = ({ name, iso2, dialCode }) => {
        clientPhonePrefix.value = dialCode;
    };

    const isPhoneValid = computed(() => {
        const maskedPattern = /^\d{3} \d{3}-\d{2}-\d{2}$/;
        const plainDigitsPattern = /^\d{10}$/;

        return maskedPattern.test(clientPhone.value) || plainDigitsPattern.test(clientPhone.value);
    });

    const isFormValid = computed(() =>
        clientName.value &&
        pickupCity.value &&
        clientPhone.value &&
        recaptchaToken.value &&
        isPhoneValid.value
    );


    let refParam = ref(null)
    let markerParam = ref(null)
    let traceIdParam = ref(null)
    let partnerParam = ref(null)

    const clientName = ref('');
    const pickupCity = ref(props.locationName ? props.locationName : '');
    const clientPhone = ref('');

    const handleErrorCallback = () => {
        recaptchaToken.value = null;
    };
    const handleExpiredCallback = () => {
        recaptchaToken.value = null;
    };
    const handleLoadCallback = (response) => {
        recaptchaToken.value = response;
    };

    onMounted(() => {
        if (localStorage.getItem('ref')) {
            refParam.value = localStorage.getItem('ref')
        }
        if (localStorage.getItem('marker')) {
            markerParam.value = localStorage.getItem('marker')
        }
        if (localStorage.getItem('trace_id')) {
            traceIdParam.value = localStorage.getItem('trace_id')
        }
        if (localStorage.getItem('partner')) {
            partnerParam.value = localStorage.getItem('partner')
        }
    })

    const fullPrice = computed(() => {
        return props.car.rate_subtotal +
                  props.car.drop_off_location_fee +
                  props.car.pick_up_location_fee +
                  props.car.pickup_out_of_hours_fee +
                  props.car.dropoff_out_of_hours_fee;
    });

    const storeOneClickOrder = async () => {
        if (loading.value) return;
        loading.value = true;

        let full_data = {
            info: [],
            extras: {
                'pick_up_location_fee': props.car.pick_up_location_fee,
                'drop_off_location_fee': props.car.drop_off_location_fee,
                'pickup_out_of_hours_fee': props.car.pickup_out_of_hours_fee,
                'dropoff_out_of_hours_fee': props.car.dropoff_out_of_hours_fee,
                'location_change_fee': props.car.location_change_fee,
            },
            vehicle: props.car,
            equipment: []
        }
        const data = {
            clientName: clientName.value,
            pickupCity: pickupCity.value,
            clientPhonePrefix: clientPhonePrefix.value,
            clientPhone: clientPhone.value.replace(/[\s-]/g, ''),
            searchParams: cars.payload,
            currency: props.car.currency,
            total: fullPrice.value,
            fullData: JSON.stringify(full_data),
            ref: refParam.value,
            marker: markerParam.value,
            trace_id: traceIdParam.value,
            ref_partner_name: partnerParam.value,
            recaptcha_token: recaptchaToken.value
        };

        try {
            const isSuccess = await booking.oneClickCreate(data);

            if (isSuccess) {
                toast.success('Ваш заказ отправлен!');
            } else {
                toast.error('Произошла ошибка.');
            }

            loading.value = false;
            closePopup();

        } catch (error) {
            loading.value = false;
            toast.error('Произошла ошибка.');
            closePopup();
        }
    }
</script>

<template>
  <div v-if="isOpen" class="popup-overlay">
    <div class="one-click-popup" @click.stop>
      <div class="car-data-container">
        <h4>
          Аренда в 1 клик {{ car.model }} <span v-if="!!car.modification">{{ car.modification }}</span> {{ car.year }}
        </h4>
        <div class="close-button-container"
             @click="closePopup"
        >
          <img src="/img/close-button.svg" />
        </div>
      </div>


      <div class="car-data-container">
        <div class="car-data">
          <div class="input-container-top-row">
            <input type="text"
                   placeholder="Введите Ваше имя"
                   v-model="clientName"
            />
            <input type="text"
                   placeholder="Город подачи"
                   v-model="pickupCity"
            />
          </div>
          <div class="input-container-bottom-row">

            <vue-tel-input v-model="clientPhonePrefix"
                           @select="onSelect"
                           :default-country="defaultCountry"
                           :preferred-countries="['ru']"
            ></vue-tel-input>

            <input type="text"
                   placeholder="Номер телефона"
                   id="oneClickPopupPhoneNumber"
                   v-phone-mask
                   style="width:calc(100% - 92px);"
                   v-model="clientPhone"
                   :class="{ 'border-red-500': clientPhone && !isPhoneValid }"
            />
          </div>

          <div class="search-grid-button">
            <div class="recaptcha-container">
              <RecaptchaV2
                      @widget-id="handleWidgetId"
                      @error-callback="handleErrorCallback"
                      @expired-callback="handleExpiredCallback"
                      @load-callback="handleLoadCallback"
              />
            </div>
            <div class="form-block__button">
              <button class="button button-accent"
                      @click="storeOneClickOrder"
                      :disabled="!isFormValid"
              >
                <template v-if="loading">
                  <span class="loader"></span>
                </template>
                Забронировать
              </button>
            </div>
          </div>
        </div>
        <div class="car-photo">
          <img :src="car.images[0].image"
               v-if="!car.model_image && car.images && car.images.length > 0"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
  .button.button-accent {
    position: relative;
  }
  .loader {
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top: 2px solid white;
    width: 14px;
    height: 14px;
    animation: spin 0.8s linear infinite;
    margin-right: 8px;
    display: inline-block;
    vertical-align: middle;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  .border-red-500 {
    border: 1px solid red !important;
  }
  .recaptcha-container {
    display: inline-block;
    transform: scale(0.85);
    transform-origin: 0 0;
    width: 100%;
    max-width: 100%;
  }
  .popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100000000;
  }

  .one-click-popup {
    position: fixed;
    top: 100px;
    left: 25%;
    width: 50%;
    height: 330px;
    background: white;
    z-index: 1111111111;
    border-radius: 8px;
    padding: 30px;
    box-shadow: 0 5px 4px rgba(0, 0, 0, 0.25);
  }

  .one-click-popup .car-data-container h4 {
    font-weight: 700;
    font-size: 20px;
    margin-bottom: 20px;
    position:relative;
  }

  .one-click-popup .close-button-container {
    position: absolute;
    top: -20px;
    right: -20px;
    cursor: pointer;
  }
  .one-click-popup .more-button {
    cursor: pointer;
  }
  .one-click-popup .close-button-container img {
    width: 30px;
    height: auto;
  }
  .one-click-popup h4 {
    font-weight: bold;
    font-size: 20px;
    margin-bottom: 20px;
  }
  .one-click-popup .input-container-top-row,
  .one-click-popup .input-container-bottom-row {
    display: flex;
    justify-content: flex-start;
  }
  .one-click-popup .input-container-top-row input,
  .one-click-popup .input-container-bottom-row input,
  .one-click-popup .vue-country-select {
    display: inline-block;
    box-shadow: 0 5px 4px rgba(0, 0, 0, 0.25);
    border: 1px solid #eee;
    border-radius: 12px;
    outline: 0;
    padding-left: 15px;
    height: 40px;
    margin-bottom: 20px;
    margin-right: 20px;
  }
  .one-click-popup .vue-country-select .dropdown.open,
  .one-click-popup .vue-country-select .dropdown:hover {
    background-color: #fff;
  }
  .one-click-popup .input-container-top-row input,
  .one-click-popup .input-container-bottom-row input {
    width: calc(50% - 12px);
  }
  .one-click-popup .input-container-top-row input:last-child,
  .one-click-popup .input-container-bottom-row input:last-child {
    margin-right: 0;
  }
  .one-click-popup .search-grid-button {
    margin-top: 0;
  }
  .one-click-popup .more-button {
    width: 100%;
  }
  .one-click-popup .more-button .fa-spinner {
    margin-right: 10px;
  }
  .one-click-popup .car-data-container {
    display: flex;
    position: relative;
  }
  .one-click-popup .car-data {
    flex-grow: 2;
  }
  .one-click-popup .car-photo {
    flex-grow: 1;
    position: relative;
  }
  .one-click-popup .car-photo img {
    max-width: 100%;
    width: 200px;
    top: 0;
    right: 0;
    display: unset;
    position: relative;
  }

  @media (min-width: 1400px) {
    .one-click-popup {
      max-width: 1000px;
    }
    .one-click-popup .car-photo img {
      width: 300px;
    }
    .one-click-popup .search-list-image {
      width: 100%;
    }
    .one-click-popup .search-list-image img {
      max-height: unset;
    }
  }
  @media (min-width: 2000px) {
    .one-click-popup {
      left: calc(50% - 500px);
    }
  }
  @media (max-width: 1200px) {
    .one-click-popup {
      left: 10%;
      width: 80%;
      height: 360px;
    }
  }
  @media (max-width: 768px) {
    .one-click-popup {
      left: 0;
      width: 100%;
      border-radius: 0;
      height: unset;
    }
    .one-click-popup .car-photo {
      display: none;
    }
    .one-click-popup .input-container-top-row {
      display: block;
    }
    .one-click-popup .input-container-top-row input {
      width: 100%;
    }
  }
</style>